import { useEffect, useRef } from 'react';
import { DateRange } from '@/components/DateRangePicker';

interface UseAutoRefreshOptions {
    /** 刷新间隔（毫秒），默认 5000ms */
    interval?: number;
    /** 是否启用自动刷新，默认 true */
    enabled?: boolean;
}

/**
 * 自动刷新 Hook
 * 每隔指定时间调用回调函数，第一次不调用
 */
export const useAutoRefresh = (
    callback: (dateRange: DateRange) => void | Promise<void>,
    dateRange: DateRange | undefined,
    options: UseAutoRefreshOptions = {}
) => {
    const { interval = 5000, enabled = true } = options;

    const isFirstRef = useRef(true);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const callbackRef = useRef(callback);

    // 保持 callback 引用最新
    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    useEffect(() => {
        if (!enabled || !dateRange) {
            return;
        }

        // 第一次不调用，直接设置定时器
        if (isFirstRef.current) {
            isFirstRef.current = false;
        }

        const timer = setInterval(async () => {
            try {
                await callbackRef.current(dateRange);
            } catch (error) {
                console.error('自动刷新出错:', error);
            }
        }, interval);

        timeoutRef.current = timer;

        return () => {
            if (timeoutRef.current) {
                clearInterval(timeoutRef.current);
            }
        };
    }, [dateRange, interval, enabled]);
};
